import { X } from 'lucide-react';
import type { WishlistLikeUser } from '../../types/wishlist';

interface LikedByModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  users: WishlistLikeUser[];
  isLoading?: boolean;
}

function getInitials(name: string) {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.substring(0, 2).toUpperCase();
}

export default function LikedByModal({ isOpen, onClose, title, users, isLoading }: LikedByModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="w-full max-w-sm rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 p-5">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-gray-900">Liked by</h2>
            <p className="mt-0.5 truncate text-xs text-gray-500">{title}</p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 rounded-full p-1 transition-colors hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto p-5">
          {isLoading ? (
            <p className="py-6 text-center text-sm text-gray-500">Loading...</p>
          ) : users.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">No likes yet.</p>
          ) : (
            users.map((user) => (
              <div key={user.userId} className="flex items-center gap-3">
                {user.profileImage ? (
                  <img
                    src={user.profileImage}
                    alt={user.name}
                    className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-primary/10 text-sm font-semibold text-brand-primary">
                    {getInitials(user.name)}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900">{user.name}</p>
                  <p className="text-xs text-gray-400">{new Date(user.likedAt).toLocaleString()}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
